import { formatDate } from "@/lib/date-formatting";
import { detectPresetFromDates } from "@/lib/date-utils";
import { extractStructuredFilters } from "@/lib/filter-state-utils";
import type { FilterGroup, StructuredFilter } from "@/lib/filters";
import type { FilterEntityType } from "@/lib/filters/types";

export interface FilterFieldDefinition {
  id: string;
  label: string;
}

export interface FilterColumn {
  key: string;
  title: string;
  filterType?: FilterType;
  clearingInfo?: FilterClearingInfo;
}

export interface FilterClearingInfo {
  type: "individual" | "consolidated_date_range";
  filterGroupType: FilterEntityType;
  index?: number; // For individual filters
  fieldName?: string; // For consolidated date ranges
}

export interface FilterDisplayResult {
  displayFilters: Record<string, unknown>;
  filterColumns: FilterColumn[];
  totalFilterCount: number;
}

// Filter type interfaces from ActiveFiltersDisplay
export interface DateFilter {
  from?: string;
  to?: string;
  nullFilter?: "is_null" | "is_not_null" | null;
}

export interface NumberFilter {
  min?: string;
  max?: string;
  nullFilter?: "is_null" | "is_not_null" | null;
}

export interface TextFilter {
  value?: string;
  nullFilter?: "is_null" | "is_not_null" | null;
}

export interface SelectFilter {
  values?: string[];
  nullFilter?: "is_null" | "is_not_null" | null;
}

export type FilterValue =
  | string
  | string[]
  | NumberFilter
  | DateFilter
  | TextFilter
  | SelectFilter
  | StructuredFilter
  | null
  | boolean;

export type FilterType =
  | "text"
  | "number"
  | "date"
  | "select"
  | "boolean"
  | "currency";

/**
 * Capitalize first letter of a string
 */
export function capitalizeFirstLetter(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Format a list of values with truncation for long lists (A, B +N format)
 */
function formatValueList(values: string[]): string {
  if (values.length === 0) return "";
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]}, ${values[1]}`;
  return `${values[0]}, ${values[1]} +${values.length - 2}`;
}

/**
 * Unified filter formatting function that handles all filter types
 */
export function formatFilterValue(
  value: FilterValue,
  filterType?: FilterType,
  fieldDefinitions?: FilterFieldDefinition[],
): string {
  // Handle null/undefined values
  if (value === null || value === undefined) {
    return "";
  }

  // Handle primitive values
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  // Handle array values (legacy multi-select format)
  if (Array.isArray(value)) {
    return formatValueList(value.map(String));
  }

  // Handle StructuredFilter objects
  if (typeof value === "object" && "field" in value && "op" in value) {
    return formatStructuredFilter(value as StructuredFilter, fieldDefinitions);
  }

  // Handle complex filter objects based on filterType
  if (typeof value === "object" && value !== null) {
    switch (filterType) {
      case "select":
        return formatSelectFilter(value as SelectFilter);
      case "number":
      case "currency":
        return formatNumberFilter(value as NumberFilter);
      case "date":
        return formatDateFilter(value as DateFilter);
      case "text":
        return formatTextFilter(value as TextFilter);
      default: {
        // Handle filters that only have nullFilter (fallback case)
        const filterObj = value as { nullFilter?: "is_null" | "is_not_null" };
        if (filterObj.nullFilter === "is_null") {
          return "is null";
        } else if (filterObj.nullFilter === "is_not_null") {
          return "is not null";
        }
        return "Custom filter";
      }
    }
  }

  return String(value);
}

/**
 * Format StructuredFilter objects (from metric card system)
 */
function formatStructuredFilter(
  filter: StructuredFilter,
  fieldDefinitions?: FilterFieldDefinition[],
): string {
  const fieldDef = fieldDefinitions?.find((f) => f.id === filter.field);

  // Special handling for touchpoint timestamp filters - they don't need field definitions
  const isTimestampFilter = filter.field === "timestamp";

  // Show loading state only for non-timestamp filters that need field definitions
  if (!isTimestampFilter && !fieldDef?.label) {
    return "Loading...";
  }

  // Use field definition label if available, otherwise use the field ID as fallback
  const fieldName = fieldDef?.label
    ? capitalizeFirstLetter(fieldDef.label)
    : capitalizeFirstLetter((filter.field || "unknown").replace(/_/g, " "));

  if (filter.op === "IS_NULL") {
    return `${fieldName} is empty`;
  } else if (filter.op === "IS_NOT_NULL") {
    return `${fieldName} is not empty`;
  } else if (filter.values && filter.values.length > 0) {
    // Check for relative date presets on BETWEEN and GTE operators
    if (filter.op === "BETWEEN" && filter.values.length >= 2) {
      const presetLabel = detectPresetFromDates(
        String(filter.values[0]),
        String(filter.values[1]),
      );
      if (presetLabel) {
        return `${fieldName}: ${presetLabel}`;
      }
    } else if (filter.op === "GTE") {
      const presetLabel = detectPresetFromDates(String(filter.values[0]));
      if (presetLabel) {
        return `${fieldName}: ${presetLabel}`;
      }
    }

    // Format values with truncation for long lists
    const formattedValues = formatValueList(
      filter.values.map((val) => String(val)),
    );
    switch (filter.op) {
      case "IN":
        return `${fieldName}: ${formattedValues}`;
      case "NOT_IN":
        return `${fieldName} not: ${formattedValues}`;
      case "GT":
        return `${fieldName} > ${formattedValues}`;
      case "GTE":
        return `${fieldName} >= ${formattedValues}`;
      case "LT":
        return `${fieldName} < ${formattedValues}`;
      case "LTE":
        return `${fieldName} <= ${formattedValues}`;
      case "BETWEEN":
        return `${fieldName} between ${formattedValues}`;
      case "LIKE":
      case "ILIKE":
        return `${fieldName} contains "${formattedValues}"`;
      case "STARTS_WITH":
        return `${fieldName} starts with "${formattedValues}"`;
      case "ENDS_WITH":
        return `${fieldName} ends with "${formattedValues}"`;
      case "EQ":
        return `${fieldName} = ${formattedValues}`;
      case "NEQ":
        return `${fieldName} != ${formattedValues}`;
      default:
        return `${fieldName}: ${formattedValues}`;
    }
  }

  return `${fieldName}: ${filter.op}`;
}

/**
 * Format SelectFilter objects
 */
function formatSelectFilter(filter: SelectFilter): string {
  const parts: string[] = [];

  if (filter.nullFilter === "is_null") {
    parts.push("is null");
  } else if (filter.nullFilter === "is_not_null") {
    parts.push("is not null");
  }

  if (filter.values && filter.values.length > 0) {
    parts.push(formatValueList(filter.values));
  }

  return parts.join(", ");
}

/**
 * Format NumberFilter objects
 */
function formatNumberFilter(filter: NumberFilter): string {
  const parts: string[] = [];

  if (filter.nullFilter === "is_null") {
    parts.push("is null");
  } else if (filter.nullFilter === "is_not_null") {
    parts.push("is not null");
  }

  if (filter.min !== undefined && filter.max !== undefined) {
    parts.push(`${filter.min} - ${filter.max}`);
  } else if (filter.min !== undefined) {
    parts.push(`≥ ${filter.min}`);
  } else if (filter.max !== undefined) {
    parts.push(`≤ ${filter.max}`);
  }

  return parts.join(", ");
}

/**
 * Format DateFilter objects
 */
function formatDateFilter(filter: DateFilter): string {
  const parts: string[] = [];

  if (filter.nullFilter === "is_null") {
    parts.push("is null");
  } else if (filter.nullFilter === "is_not_null") {
    parts.push("is not null");
  }

  if (filter.from !== undefined && filter.to !== undefined) {
    parts.push(`${formatDate(filter.from)} - ${formatDate(filter.to)}`);
  } else if (filter.from !== undefined) {
    parts.push(`From ${formatDate(filter.from)}`);
  } else if (filter.to !== undefined) {
    parts.push(`Until ${formatDate(filter.to)}`);
  }

  return parts.join(", ");
}

/**
 * Format TextFilter objects
 */
function formatTextFilter(filter: TextFilter): string {
  const parts: string[] = [];

  if (filter.nullFilter === "is_null") {
    parts.push("is null");
  } else if (filter.nullFilter === "is_not_null") {
    parts.push("is not null");
  }

  if (filter.value !== undefined && filter.value.trim() !== "") {
    parts.push(`Contains "${filter.value}"`);
  }

  return parts.join(", ");
}

/**
 * Legacy function - now delegates to formatFilterValue
 * @deprecated Use formatFilterValue instead
 */
export function formatFilterDisplay(
  filter: StructuredFilter,
  fieldDefinitions: FilterFieldDefinition[],
): string {
  return formatFilterValue(filter, undefined, fieldDefinitions);
}

/**
 * Consolidate date range filters for the same field into a single display
 */
function consolidateDateRangeFilters(
  filters: StructuredFilter[],
  fieldDefinitions?: FilterFieldDefinition[],
  filterGroupType: FilterEntityType = "touchpoint",
): Array<{
  key: string;
  value: string;
  field: string;
  clearingInfo: FilterClearingInfo;
}> {
  const consolidated: Array<{
    key: string;
    value: string;
    field: string;
    clearingInfo: FilterClearingInfo;
  }> = [];
  const dateRanges: Record<string, { from?: string; to?: string }> = {};
  const processedFields = new Set<string>();

  // Group filters by field and identify date ranges
  filters.forEach((filter, index) => {
    if (!filter.field) return;

    // Check if this is a date range filter (GTE/LTE with date-like values)
    const isDateRangeFilter =
      (filter.op === "GTE" || filter.op === "LTE") &&
      filter.values &&
      filter.values.length > 0 &&
      typeof filter.values[0] === "string" &&
      /^\d{4}-\d{2}-\d{2}/.test(filter.values[0]);

    if (isDateRangeFilter) {
      if (!dateRanges[filter.field]) {
        dateRanges[filter.field] = {};
      }

      if (filter.op === "GTE" && filter.values) {
        dateRanges[filter.field].from = filter.values[0] as string;
      } else if (filter.op === "LTE" && filter.values) {
        dateRanges[filter.field].to = filter.values[0] as string;
      }
    } else if (!processedFields.has(filter.field)) {
      // Regular filter - add it directly
      consolidated.push({
        key: `${filterGroupType}_${index}`,
        value: formatFilterValue(filter, undefined, fieldDefinitions),
        field: filter.field,
        clearingInfo: {
          type: "individual",
          filterGroupType,
          index,
        },
      });
      processedFields.add(filter.field);
    }
  });

  // Add consolidated date ranges
  Object.entries(dateRanges).forEach(([field, range]) => {
    const fieldDef = fieldDefinitions?.find((f) => f.id === field);
    const fieldName = fieldDef?.label
      ? capitalizeFirstLetter(fieldDef.label)
      : capitalizeFirstLetter(field.replace(/_/g, " "));

    let rangeValue = "";
    if (range.from && range.to) {
      // Check for relative date preset
      const presetLabel = detectPresetFromDates(range.from, range.to);
      rangeValue =
        presetLabel ?? `${formatDate(range.from)} - ${formatDate(range.to)}`;
    } else if (range.from) {
      // Check for single-date preset (e.g., "Today" with just GTE)
      const presetLabel = detectPresetFromDates(range.from);
      rangeValue = presetLabel
        ? `From ${presetLabel}`
        : `From ${formatDate(range.from)}`;
    } else if (range.to) {
      rangeValue = `Until ${formatDate(range.to)}`;
    }

    if (rangeValue) {
      // Special handling for touchpoint timestamp - don't include field name
      const displayValue =
        field === "timestamp" ? rangeValue : `${fieldName}: ${rangeValue}`;

      consolidated.push({
        key: `daterange_${field}`,
        value: displayValue,
        field: field,
        clearingInfo: {
          type: "consolidated_date_range",
          filterGroupType,
          fieldName: field,
        },
      });
    }
  });

  return consolidated;
}

/**
 * Process metric card filters for ActiveFiltersDisplay
 */
export function processMetricCardFilters(
  accountFilters: FilterGroup,
  opportunityFilters: FilterGroup,
  touchpointFilters: FilterGroup,
  hasAccountFilters: boolean,
  hasOpportunityFilters: boolean,
  hasTouchpointFilters: boolean,
  accountFields: FilterFieldDefinition[],
  opportunityFields: FilterFieldDefinition[],
  touchpointFields: FilterFieldDefinition[] = [],
  campaignFilters?: FilterGroup,
  hasCampaignFilters?: boolean,
  campaignFields?: FilterFieldDefinition[],
): FilterDisplayResult {
  const displayFilters: Record<string, unknown> = {};
  const filterColumns: FilterColumn[] = [];

  // Process account filters (no consolidation)
  if (hasAccountFilters) {
    const structuredFilters = extractStructuredFilters(accountFilters);
    structuredFilters.forEach((filter, index) => {
      const key = `account_${index}`;
      displayFilters[key] = formatFilterValue(filter, undefined, accountFields);
      filterColumns.push({
        key,
        title: "Account",
        filterType: "text",
        clearingInfo: {
          type: "individual",
          filterGroupType: "account",
          index,
        },
      });
    });
  }

  // Process opportunity filters (no consolidation)
  if (hasOpportunityFilters) {
    const structuredFilters = extractStructuredFilters(opportunityFilters);
    structuredFilters.forEach((filter, index) => {
      const key = `opportunity_${index}`;
      displayFilters[key] = formatFilterValue(
        filter,
        undefined,
        opportunityFields,
      );
      filterColumns.push({
        key,
        title: "Opportunity",
        filterType: "text",
        clearingInfo: {
          type: "individual",
          filterGroupType: "opportunity",
          index,
        },
      });
    });
  }

  // Process touchpoint filters with consolidation
  if (hasTouchpointFilters) {
    const structuredFilters = extractStructuredFilters(touchpointFilters);
    const consolidatedFilters = consolidateDateRangeFilters(
      structuredFilters,
      touchpointFields,
      "touchpoint",
    );

    consolidatedFilters.forEach((consolidatedFilter) => {
      displayFilters[consolidatedFilter.key] = consolidatedFilter.value;
      filterColumns.push({
        key: consolidatedFilter.key,
        title: "Touchpoint",
        filterType: "text",
        clearingInfo: consolidatedFilter.clearingInfo,
      });
    });
  }

  // Process campaign filters (no consolidation)
  if (hasCampaignFilters && campaignFilters) {
    const structuredFilters = extractStructuredFilters(campaignFilters);
    structuredFilters.forEach((filter, index) => {
      const key = `campaign_${index}`;
      displayFilters[key] = formatFilterValue(
        filter,
        undefined,
        campaignFields || [],
      );
      filterColumns.push({
        key,
        title: "Campaign",
        filterType: "text",
        clearingInfo: {
          type: "individual",
          filterGroupType: "campaign",
          index,
        },
      });
    });
  }

  const totalFilterCount =
    extractStructuredFilters(accountFilters).length +
    extractStructuredFilters(opportunityFilters).length +
    extractStructuredFilters(touchpointFilters).length +
    (campaignFilters ? extractStructuredFilters(campaignFilters).length : 0);

  return {
    displayFilters,
    filterColumns,
    totalFilterCount,
  };
}
